import type { ReactNode } from 'react';
import { useSessionState } from './session-context.tsx';

/**
 * Renders its page only once there is a session, and a frame until then.
 *
 * This exists so the router can mount **before** authentication resolves.
 * It used not to: `main.tsx` held the whole application back behind
 * `Loading…` until Firebase Auth had initialised, read IndexedDB and the
 * memberships query had answered. On the portal dashboard that measured as
 * first contentful paint 2.6 s and largest contentful paint 4.8 s with
 * every byte delivered by 30 ms — the application waiting on itself
 * (P4-25).
 *
 * The frame is deliberately plain. A skeleton that mimicked the page would
 * be a second layout to keep in step with the first, and getting it wrong
 * is a layout shift rather than a saved one.
 *
 * Pages still use `useSession()` and still get a guaranteed session: this
 * is what guarantees it. The route guards are unchanged and re-run when
 * the status settles, so a sub who typed `/app` is still sent to `/sub` —
 * a moment later than before, with a frame on screen rather than a blank
 * page.
 */
export function RequireSession({ children }: { readonly children: ReactNode }) {
  const state = useSessionState();
  if (state.status === 'ready') return <>{children}</>;

  return (
    <div className="min-h-full bg-slate-50">
      <div className="h-12 bg-slate-900" />
      <div className="flex flex-col gap-4 p-6">
        {/*
          `aria-busy` with a live region rather than a silent spinner: a
          screen reader should hear that something is coming, and hear it
          once rather than on every poll.
        */}
        <p role="status" aria-busy="true" className="text-sm text-slate-600">
          Loading…
        </p>
      </div>
    </div>
  );
}
